import React from "react";

import { trpc } from "@blis/trpc/react";

import { CommandMenuProvider } from "@/components/CommandMenu";
import { SubHeader } from "@/components/SubHeader";
import { Breadcrumb, Breadcrumbs } from "@/components/ui/breadcrumbs";
import { Button } from "@/components/ui/button";
import { Centered } from "@/components/ui/container";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/field";
import { Modal } from "@/components/ui/modal";
import { Note } from "@/components/ui/note";
import {
  Select,
  SelectItem,
  SelectList,
  SelectTrigger,
} from "@/components/ui/select";
import { TabPanel, Tabs, TabsList } from "@/components/ui/tabs";
import { mutationResponse } from "@/utils/trpc";
import type { TestRunDetails, TestRunWorkspaceDetails } from "../types";
import { WorkflowStep } from "./WorkflowStep";
import { WorkspaceActions } from "./WorkspaceActions";
import { WorkspaceOverview } from "./WorkspaceOverview";
import { useWorkspace, WorkspaceProvider } from "./WorkspaceProvider";
import { WorkspaceSidebar } from "./WorkspaceSidebar";

export function TestRunWorkspace({
  workspace,
  testRun,
}: {
  workspace: TestRunWorkspaceDetails;
  testRun: TestRunDetails;
}) {
  return (
    <WorkspaceProvider workspace={workspace} testRun={testRun}>
      <SubHeader>
        <SubHeader.Left>
          <Breadcrumbs>
            <Breadcrumb href={{ to: "/runs" }}>Test runs</Breadcrumb>
            <Breadcrumb>Test run {testRun.id}</Breadcrumb>
          </Breadcrumbs>
        </SubHeader.Left>
        {/* <SubHeader.Right>
          <WorkspaceMenu />
        </SubHeader.Right> */}
      </SubHeader>
      <div className="flex flex-1 items-stretch min-h-0">
        <WorkspaceSidebar />
        <WorkspacePanels />
      </div>
      <UpdateProcessConfirmation />
      <CommandMenuProvider>
        <WorkspaceActions />
      </CommandMenuProvider>
    </WorkspaceProvider>
  );
}

function WorkspacePanels() {
  const { activeTab, workflow } = useWorkspace();

  return (
    <Tabs selectedKey={activeTab} className="flex-1 flex flex-col">
      <TabsList className="hidden" />
      <TabPanel id="overview" className="flex flex-col flex-1 mt-0">
        <WorkspaceOverview />
      </TabPanel>
      {workflow.instanceTemplate.steps.map((step) => (
        <TabPanel
          key={step.id}
          id={step.id}
          className="flex flex-col flex-1 mt-0 overflow-y-auto"
        >
          <WorkflowStep step={step} />
        </TabPanel>
      ))}
    </Tabs>
  );
}

export function NoTestOrderError({ testRun }: { testRun: TestRunDetails }) {
  return (
    <>
      <SubHeader>
        <SubHeader.Left>
          <Breadcrumbs>
            <Breadcrumb href={{ to: "/runs" }}>Test runs</Breadcrumb>
            <Breadcrumb>Test run {testRun.id}</Breadcrumb>
          </Breadcrumbs>
        </SubHeader.Left>
      </SubHeader>
      <Centered>
        <div className="text-lg font-medium">
          No tests have been assigned to this test run.
        </div>
      </Centered>
    </>
  );
}

function UpdateProcessConfirmation() {
  const [version, setVersion] = React.useState<number | null>(null);
  const { testRun, workflow, updateProcess, setUpdateProcess } = useWorkspace();

  const { data } = trpc.process.getProcessTemplateVersions.useQuery({
    versionId: workflow.templateVersionId,
  });

  const options = data?.versions.map((v, idx) => ({
    label: `v${v.version}${idx === 0 ? " (latest)" : ""}`,
    value: v.id,
  }));

  const update = trpc.process.updateProcessForRun.useMutation(
    mutationResponse({
      errorTitle: "Failed to update process",
      successMessage: "Process updated",
      onSuccess: () => {
        setUpdateProcess(false);
      },
    }),
  );

  const onConfirm = () => {
    if (!version) {
      return;
    }

    update.mutate({
      testRunId: testRun.id,
      versionId: version,
    });
  };

  return (
    <Modal isOpen={updateProcess} onOpenChange={setUpdateProcess}>
      <Dialog>
        <DialogHeader>
          <DialogTitle>Update process</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Note intent="warning">
            This will overwrite the process for this test run to the selected
            version. This is a dangerous action and irreversible.
          </Note>
          <Select
            selectedKey={version}
            onSelectionChange={(key) => setVersion(key as number)}
          >
            <Label>Version</Label>
            <SelectTrigger />
            <SelectList items={options}>
              {(item) => <SelectItem id={item.value}>{item.label}</SelectItem>}
            </SelectList>
          </Select>
        </DialogBody>
        <DialogFooter>
          <Button
            onClick={onConfirm}
            isDisabled={!version}
            isPending={update.isPending}
          >
            Update
          </Button>
          <DialogCloseButton>Cancel</DialogCloseButton>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}
